import { useEffect, useState } from 'react';
import { schedules, playlists, type Schedule, type Playlist } from '../../api';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export function SchedulesPage() {
  const [list, setList] = useState<Schedule[]>([]);
  const [pls, setPls] = useState<Playlist[]>([]);
  const [editing, setEditing] = useState<Schedule | 'new' | null>(null);

  async function refresh() {
    const [sc, pl] = await Promise.all([schedules.list(), playlists.list()]);
    setList(sc.schedules);
    setPls(pl.playlists);
  }
  useEffect(() => { refresh(); }, []);

  async function remove(id: string) {
    if (!confirm('Delete this schedule? Devices assigned to it will become unscheduled.')) return;
    await schedules.remove(id);
    refresh();
  }

  return (
    <div>
      <div className="toolbar">
        <h1>Schedules</h1>
        <div className="spacer" />
        <button className="primary" onClick={() => setEditing('new')}>+ New schedule</button>
      </div>
      {list.length === 0 ? (
        <div className="empty">No schedules yet. A schedule has a default playlist and optional time-of-day rules.</div>
      ) : (
        <table>
          <thead><tr><th>Name</th><th>Default playlist</th><th>Rules</th><th></th></tr></thead>
          <tbody>
            {list.map((s) => (
              <tr key={s.id}>
                <td><strong>{s.name}</strong></td>
                <td>{pls.find((p) => p.id === s.default_playlist_id)?.name ?? '—'}</td>
                <td>{s.rules.length}</td>
                <td style={{ textAlign: 'right' }}>
                  <button onClick={() => setEditing(s)}>Edit</button>{' '}
                  <button className="danger" onClick={() => remove(s.id)}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {editing && (
        <ScheduleEditor
          schedule={editing === 'new' ? null : editing}
          playlists={pls}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); refresh(); }}
        />
      )}
    </div>
  );
}

function ScheduleEditor({
  schedule, playlists: pls, onClose, onSaved,
}: { schedule: Schedule | null; playlists: Playlist[]; onClose: () => void; onSaved: () => void }) {
  const [name, setName] = useState(schedule?.name ?? '');
  const [defaultId, setDefaultId] = useState<string | null>(schedule?.default_playlist_id ?? null);
  const [rules, setRules] = useState(schedule?.rules ?? []);
  const [busy, setBusy] = useState(false);

  function addRule() {
    if (pls.length === 0) return;
    setRules([...rules, {
      playlist_id: pls[0].id, days: [1,2,3,4,5],
      start_time: '08:00', end_time: '17:00', priority: 0,
    }]);
  }
  function updateRule(i: number, patch: Partial<Schedule['rules'][number]>) {
    setRules(rules.map((r, idx) => idx === i ? { ...r, ...patch } : r));
  }
  function toggleDay(i: number, day: number) {
    const r = rules[i];
    const has = r.days.includes(day);
    updateRule(i, { days: has ? r.days.filter((d) => d !== day) : [...r.days, day].sort() });
  }
  function removeRule(i: number) { setRules(rules.filter((_, idx) => idx !== i)); }

  async function save() {
    setBusy(true);
    try {
      if (schedule) {
        await schedules.update(schedule.id, { name, rules, default_playlist_id: defaultId });
      } else {
        await schedules.create({ name, rules, default_playlist_id: defaultId });
      }
      onSaved();
    } finally { setBusy(false); }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ width: 680 }}>
        <h2 style={{ marginTop: 0 }}>{schedule ? 'Edit schedule' : 'New schedule'}</h2>
        <div className="field"><label>Name</label><input value={name} onChange={(e) => setName(e.target.value)} autoFocus /></div>
        <div className="field">
          <label>Default playlist (plays whenever no rule matches)</label>
          <select value={defaultId ?? ''} onChange={(e) => setDefaultId(e.target.value || null)}>
            <option value="">(none)</option>
            {pls.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>

        <label>Rules</label>
        <div style={{ border: '1px solid var(--border)', borderRadius: 6, padding: '0.6em', minHeight: 60 }}>
          {rules.length === 0 && <div style={{ color: 'var(--fg-3)', textAlign: 'center', padding: '0.6em' }}>No rules. Default playlist will always play.</div>}
          {rules.map((r, i) => (
            <div key={i} style={{ background: 'var(--bg-2)', padding: '0.7em', borderRadius: 6, marginBottom: '0.5em' }}>
              <div style={{ display: 'flex', gap: '0.5em', marginBottom: '0.5em', alignItems: 'center' }}>
                <select value={r.playlist_id} onChange={(e) => updateRule(i, { playlist_id: e.target.value })} style={{ flex: 1 }}>
                  {pls.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
                <input type="time" value={r.start_time} onChange={(e) => updateRule(i, { start_time: e.target.value })} style={{ width: 120 }} />
                <span>→</span>
                <input type="time" value={r.end_time} onChange={(e) => updateRule(i, { end_time: e.target.value })} style={{ width: 120 }} />
                <button className="danger" onClick={() => removeRule(i)}>×</button>
              </div>
              <div style={{ display: 'flex', gap: '0.3em' }}>
                {DAYS.map((d, idx) => (
                  <button key={idx} onClick={() => toggleDay(i, idx)}
                    style={{
                      padding: '0.25em 0.55em',
                      fontSize: '0.8em',
                      background: r.days.includes(idx) ? 'var(--accent)' : 'var(--bg-1)',
                      color: r.days.includes(idx) ? '#061018' : 'var(--fg-2)',
                      borderColor: r.days.includes(idx) ? 'transparent' : 'var(--border)',
                    }}>{d}</button>
                ))}
              </div>
            </div>
          ))}
        </div>
        <button onClick={addRule} disabled={pls.length === 0} style={{ marginTop: '0.6em' }}>+ Add rule</button>

        <div style={{ display: 'flex', gap: '0.6em', justifyContent: 'flex-end', marginTop: '1.2em' }}>
          <button onClick={onClose}>Cancel</button>
          <button className="primary" disabled={!name || busy} onClick={save}>{busy ? 'Saving...' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
}
